import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, Bug, MagnifyingGlass, X } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { DotLoader } from '@/components/ui/DotLoader'
import { cn } from '@/lib/utils'
import { adminBugsApi, adminMetaApi } from '../adminApi'
import {
  DEFAULT_FILTERS,
  type BugFilters,
  type SortKey,
  filtersFromSearch,
  hasActiveFilters,
  pageCount,
  searchFromFilters,
  toListParams,
  toggleSort,
} from '../lib/bugFilters'
import { bugStatusLabel, formatDate } from '../lib/labels'
import {
  BUG_STATUS_TONE,
  Badge,
  EmptyState,
  PageHeader,
  Panel,
  SEVERITY_TONE,
  bodyStyle,
  metaStyle,
  rowClass,
  tbodyClass,
  tdClass,
  thClass,
  thStyle,
} from '../components/ui'
import type { BugListItem } from '../types'

const COLUMNS: { key: SortKey | null; label: string; className?: string }[] = [
  { key: 'reference_code', label: 'Reference', className: 'w-40' },
  { key: null, label: 'Reporter' },
  { key: 'module', label: 'Module', className: 'w-32' },
  { key: null, label: 'Summary' },
  { key: 'severity', label: 'Severity', className: 'w-28' },
  { key: 'status', label: 'Status', className: 'w-32' },
  { key: 'assignee', label: 'Assignee', className: 'w-36' },
  { key: 'created_at', label: 'Reported', className: 'w-40' },
]

export default function BugsListPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => filtersFromSearch(searchParams), [searchParams])
  const [q, setQ] = useState(filters.q)

  useEffect(() => {
    setQ(filters.q)
  }, [filters.q])

  const apply = (next: BugFilters) => setSearchParams(searchFromFilters(next), { replace: true })
  const set = (patch: Partial<BugFilters>) => apply({ ...filters, ...patch, page: 1 })

  const meta = useQuery({ queryKey: ['admin', 'meta'], queryFn: () => adminMetaApi.get().then((r) => r.data) })
  const bugs = useQuery({
    queryKey: ['admin', 'bugs', toListParams(filters)],
    queryFn: () => adminBugsApi.list(toListParams(filters)).then((r) => r.data),
    keepPreviousData: true,
  })

  const withAny = (label: string, values: { value: string; label: string }[]) => [{ value: '', label }, ...values]
  const statusOptions = withAny(
    'All statuses',
    (meta.data?.bug_statuses ?? ['todo', 'in_progress', 'resolved']).map((s) => ({ value: s, label: bugStatusLabel(s) })),
  )
  const moduleOptions = withAny('All modules', (meta.data?.modules ?? []).map((m) => ({ value: m, label: m })))
  const severityOptions = withAny('All severities', (meta.data?.severities ?? []).map((s) => ({ value: s, label: s })))
  const assigneeOptions = withAny('All assignees', [
    { value: '__unassigned__', label: 'Unassigned' },
    ...(meta.data?.assignees ?? []).map((a) => ({ value: a.name, label: a.name })),
  ])

  const total = bugs.data?.total ?? 0
  const pages = pageCount(total)

  return (
    <div className="space-y-5">
      <PageHeader
        title="Bugs"
        description={bugs.data ? `${total} report${total === 1 ? '' : 's'} · click a row to triage` : 'Loading…'}
      />

      <form
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          set({ q: q.trim() })
        }}
      >
        <div className="lg:col-span-2">
          <Input
            aria-label="Search"
            placeholder="Search reference, summary or reporter"
            icon={<MagnifyingGlass size={15} />}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onBlur={() => q.trim() !== filters.q && set({ q: q.trim() })}
          />
        </div>
        <Select ariaLabel="Status" value={filters.status} onChange={(v) => set({ status: v })} options={statusOptions} />
        <Select ariaLabel="Module" value={filters.module} onChange={(v) => set({ module: v })} options={moduleOptions} />
        <Select ariaLabel="Severity" value={filters.severity} onChange={(v) => set({ severity: v })} options={severityOptions} />
        <div className="flex gap-2">
          <Select ariaLabel="Assignee" value={filters.assignee} onChange={(v) => set({ assignee: v })} options={assigneeOptions} className="flex-1" />
          {hasActiveFilters(filters) && (
            <Button type="button" variant="ghost" size="sm" onClick={() => apply(DEFAULT_FILTERS)} icon={<X size={14} />}>
              Clear
            </Button>
          )}
        </div>
      </form>

      <Panel className="overflow-x-auto">
        <table className="w-full" style={bodyStyle}>
          <thead>
            <tr className="border-b border-border/50 dark:border-white/[0.06]">
              {COLUMNS.map((c) => (
                <th key={c.label} className={cn(thClass, c.className)} style={thStyle}>
                  {c.key ? (
                    <button
                      type="button"
                      className={cn('inline-flex items-center gap-1 hover:text-foreground', filters.sort === c.key && 'text-foreground')}
                      onClick={() => apply(toggleSort(filters, c.key as SortKey))}
                    >
                      {c.label}
                      {filters.sort === c.key && (filters.order === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                    </button>
                  ) : (
                    c.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={tbodyClass}>
            {bugs.isLoading && (
              <tr>
                <td colSpan={COLUMNS.length} className="py-14 text-center">
                  <DotLoader />
                </td>
              </tr>
            )}
            {bugs.isError && (
              <tr>
                <td colSpan={COLUMNS.length} className="py-14 text-center text-red-700 dark:text-red-300">
                  Could not load bugs.{' '}
                  <button type="button" className="underline" onClick={() => bugs.refetch()}>
                    Retry
                  </button>
                </td>
              </tr>
            )}
            {bugs.data && bugs.data.items.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length}>
                  <EmptyState icon={<Bug size={26} />}>
                    {hasActiveFilters(filters) ? 'No bugs match these filters.' : 'No bug reports yet.'}
                  </EmptyState>
                </td>
              </tr>
            )}
            {bugs.data?.items.map((b: BugListItem) => (
              <tr key={b.id} className={rowClass}>
                <td className={cn(tdClass, 'font-mono')} style={metaStyle}>
                  <Link to={`/admin/bugs/${b.id}`} className="text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline">
                    {b.reference_code ?? `#${b.id}`}
                  </Link>
                </td>
                <td className={tdClass}>
                  <div className="truncate max-w-[220px]" title={b.reporter.email ?? ''}>
                    {b.reporter.email ?? `user ${b.reporter.user_id}`}
                  </div>
                  {b.reporter.account_name && (
                    <div className="text-muted-foreground dark:text-[#8a8f98] truncate max-w-[220px]" style={metaStyle}>
                      {b.reporter.account_name}
                    </div>
                  )}
                </td>
                <td className={tdClass}>{b.module ?? '—'}</td>
                <td className={tdClass}>
                  <Link to={`/admin/bugs/${b.id}`} className="block truncate max-w-[380px] hover:underline" title={b.title}>
                    {b.title}
                  </Link>
                </td>
                <td className={tdClass}>{b.severity ? <Badge tone={SEVERITY_TONE[b.severity] ?? 'neutral'}>{b.severity}</Badge> : '—'}</td>
                <td className={tdClass}>
                  <Badge tone={BUG_STATUS_TONE[b.admin_status] ?? 'neutral'}>{bugStatusLabel(b.admin_status)}</Badge>
                </td>
                <td className={tdClass}>{b.assignee ?? <span className="text-muted-foreground dark:text-[#8a8f98]">Unassigned</span>}</td>
                <td className={cn(tdClass, 'text-muted-foreground dark:text-[#8a8f98] whitespace-nowrap')} style={metaStyle}>
                  {formatDate(b.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      {pages > 1 && (
        <div className="flex items-center justify-between" style={bodyStyle}>
          <span className="text-muted-foreground dark:text-[#8a8f98]">
            Page {filters.page} of {pages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={filters.page <= 1} onClick={() => apply({ ...filters, page: filters.page - 1 })}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={filters.page >= pages} onClick={() => apply({ ...filters, page: filters.page + 1 })}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
